import React, { useState, useEffect } from "react";
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    Tab,
    Tabs,
    CircularProgress
} from "@mui/material";
import useAxios from '../../../../hooks/useAxios';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import { useSelector, useDispatch } from "react-redux";
import { addPurchasedStock } from '../../../../Actions/PurchasedStocks';
import PurchasedStocks from "../PurchasedStocks";
import SingleStockInvestment from './SingleStockInvestment';
import BulkStockInvestment from './BulkStockInvestment';


function TabPanel({ value, index, children }) {
    return value === index && <Box sx={{ p: 3 }}>{children}</Box>;
}

const MOMENTUM = "momentum";

const MomentumTrading = ({ stocks }) => {
    const momentumStocks = stocks[0].data;
    const [tab, setTab] = React.useState(0);
    const placeBulkOrderURL = `${process.env.REACT_APP_APP_TRADE_URL}/trade/execute-bulk`;
    const getInvestmentReviewURL = `${process.env.REACT_APP_APP_TRADE_URL}/data/investment-preview`;
    const getSinglePriceURL = `${process.env.REACT_APP_APP_TRADE_URL}/data/price`;

    const token = useSelector((state) => state.user_authentication.token);
    const [bulkAmount, setBulkAmount] = useState(null);
    const [previewData, setPreviewData] = useState([]);
    const [selectedStock, setSelectedStock] = useState([]);
    const [selectedStockCurrentPrice, setSelectedStockCurrentPrice] = useState(0);
    const [investmentAmount, setInvestmentAmount] = useState(null);
    const [orderMessage, setOrderMessage] = useState('');
    const [severity, setSeverity] = useState('');
    const [loading, setLoading] = useState(false);
    const [quantity, setQuanity] = useState(0);
    const [currentPurchased, setCurrentPurchased] = useState([]);
    const [totalValue, settotalValue] = useState(0);
    const [failedOrders, setFailedOrders] = useState('');
    const dispatch = useDispatch();

    const handleTabChange = (event, newValue) => {
        setTab(newValue);
        setOrderMessage('');
        setPreviewData([]);
    };

    const {
        data: placeBulkOrder,
        error: errorBulkOrder,
        loading: loadingBulkOrder,
        callAPI: callBulkOrderAPI
    } = useAxios();

    useEffect(() => {

        if (placeBulkOrder) {

            setLoading(false);

            if (placeBulkOrder.success === true) {
                const successfulOrders = placeBulkOrder.results.filter((item) => item.success);
                const failedStockOrders = placeBulkOrder.results.filter((item) => !item.success);

                if (successfulOrders.length > 0) {
                    setCurrentPurchased(successfulOrders);
                    successfulOrders.forEach(order => {
                        const total = +(order.quantity * order.price).toFixed(2);
                        dispatch(addPurchasedStock(
                            MOMENTUM, // 'value' or 'momentum'
                            {
                                symbol: order.symbol,
                                quantity: order.quantity,
                                total,
                                price: order.price,
                                orderType: order.order_type,
                                tradeId: order.trade_id
                            }
                        ));
                    });

                    setPreviewData([]);


                    if (failedStockOrders.length > 0) {
                        setSeverity("warning");
                        setFailedOrders(failedStockOrders);
                        setOrderMessage(`${successfulOrders.map(item => item.symbol).join(', ')} stock(s) purchased successfully. Failed to purchase stock(s): ${failedStockOrders.map(item => item.symbol).join(', ')}`);
                    } else {
                        setSeverity("success");
                        setOrderMessage(`${successfulOrders.map(item => item.symbol).join(', ')} stock(s) purchased successfully.`);
                    }

                } else if (failedStockOrders.length > 0) {
                    setFailedOrders(failedStockOrders);
                    setSeverity("error"); // Changed from "warning" to "error" for all failures
                    setOrderMessage(`All orders failed. Please review errors. Failed stock(s): ${failedOrders.map(item => item.symbol).join(', ')}`);
                } else {

                    setSeverity("warning");
                    setOrderMessage("No orders processed. Please try again.");
                }
            } else {

                setSeverity("error"); // Changed from "warning" to "error" for global failure
                setOrderMessage("Purchase failed. Please try again.");
            }
        } else if (loadingBulkOrder && errorBulkOrder) {
            console.error(errorBulkOrder);
            setLoading(false);
            setSeverity("error");
            setOrderMessage("Failed to Purchase. Please try again due to a network or server error.");
        }
    }, [placeBulkOrder, errorBulkOrder, loadingBulkOrder]);


    const {
        data: getInvestReview,
        error: errorInvestReview,
        loading: loadingInvestReview,
        callAPI: callInvestReviewAPI
    } = useAxios();

    useEffect(() => {


        if (getInvestReview) {

            setLoading(false);
            if (getInvestReview.success === true) {
                console.log(getInvestReview.preview);
                setPreviewData(getInvestReview.preview);
            } else {
                setSeverity("warning");
                setOrderMessage("Get price failed. Please try again.");
            }
        } else if (loadingInvestReview && errorInvestReview) {
            setLoading(false);
            console.error(errorInvestReview);
            setSeverity("error");
            setOrderMessage("Failed to get Price. Please try again.");
        }
    }, ([getInvestReview, errorInvestReview, loadingInvestReview]));


    const {
        data: getStockPrice,
        error: errorStockPrice,
        loading: loadingStockPrice,
        callAPI: callStockPrice
    } = useAxios();

    useEffect(() => {
        if (getStockPrice && loadingStockPrice) {

            setLoading(false);


            if (getStockPrice.success) {
                setSelectedStockCurrentPrice(getStockPrice.price);
            } else {
                setSeverity("warning");
                setOrderMessage("Get price failed. Please try again.");
            }

        } else if (loadingStockPrice && errorStockPrice) {
            console.error(errorStockPrice);
            setLoading(false);
            setSeverity("error");
            setOrderMessage("Failed to get Price. Please try again.");
        }
    }, ([getStockPrice, errorStockPrice, loadingStockPrice]));

    const totalInvestment = previewData.reduce((sum, s) => sum + s.investment, 0);
    const remaining = bulkAmount - totalInvestment;




    const generatePreview = () => {
        const payload = {
            total_investment: bulkAmount,
            stocks: momentumStocks.map(stock => stock.Stock),
            strategy: "VALUE"
        };
        setLoading(true);
        callInvestReviewAPI(
            getInvestmentReviewURL,
            "POST",
            payload,
            {
                "Authorization": "Bearer " + token,
                "Content-Type": "application/json"
            }
        )
    };



    const handleInvestmentAmount = (e) => {
        setInvestmentAmount(e.target.value);
        const qn = Math.floor(e.target.value / selectedStockCurrentPrice);
        const tValue = (qn * selectedStockCurrentPrice).toFixed(2);
        setQuanity(qn)
        settotalValue(tValue);
    }

    const handlePurchaseSingle = () => {
        const payload = {
            total_investment: investmentAmount,
            stocks: [selectedStock.Stock.replace(/\.[a-zA-Z]+$/, "")],
            strategy: "MOMENTUM",
            order_type: "BUY"
        };
        setLoading(true);
        callBulkOrderAPI(
            placeBulkOrderURL,
            "POST",
            payload,
            {
                "Authorization": "Bearer " + token,
                "Content-Type": "application/json"
            }
        )
    };


    const handlePurchaseAll = () => {
        const payload = {
            total_investment: bulkAmount,
            stocks: momentumStocks.map(stock => stock.Stock.replace(/\.[a-zA-Z]+$/, "")),
            strategy: "MOMENTUM",
            order_type: "BUY"
        };
        setLoading(true);
        callBulkOrderAPI(
            placeBulkOrderURL,
            "POST",
            payload,
            {
                "Authorization": "Bearer " + token,
                "Content-Type": "application/json"
            }
        )
    };



    const handleSelectSell = (e) => {
        setSelectedStock(momentumStocks.find((s) => s.Stock === e.target.value))
        setLoading(true);
        setSelectedStockCurrentPrice(0);
        setOrderMessage('');
        callStockPrice(
            getSinglePriceURL + `/${e.target.value}`,
            "GET",
            null,
            {
                "Authorization": "Bearer " + token,
                "Content-Type": "application/json"
            }
        )
    }

    const AvailableStocks = () => {
        return (
            <>
                <TableContainer component={Paper} sx={{ mt: 3, borderRadius: 2, boxShadow: 3 }}>
                    <Table size="small">
                        <TableHead sx={{ backgroundColor: "#2d4890" }}>
                            <TableRow>
                                {["Stock", "ROE_Average", "ROCE_Average", "PS", "PE", "PB", "Rank", "Volume", "Company", "Sector", "Industry"].map((header) => (
                                    <TableCell key={header} sx={{ color: "white", fontWeight: 600 }}>
                                        {header}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {momentumStocks.map((stock, index) => {


                                return (
                                    <TableRow
                                        key={stock.Stock}
                                        sx={{
                                            backgroundColor: index % 2 === 0 ? "#f9f9f9" : "#ffffff",
                                            "&:hover": { backgroundColor: "#f0f4ff" },
                                            transition: "background-color 0.3s ease",
                                        }}
                                    >
                                        <TableCell>{stock.Stock}</TableCell>
                                        <TableCell>{stock.ROE_Average}</TableCell>
                                        <TableCell>{stock.ROCE_Average}</TableCell>
                                        <TableCell>{stock.PS}</TableCell>
                                        <TableCell>{stock.PE}</TableCell>
                                        <TableCell>{stock.PB}</TableCell>
                                        <TableCell>{stock.Avg_Price_Rank}</TableCell>
                                        <TableCell>{stock["5 Day Avg Volume (Cr)"].toFixed(2)}</TableCell>
                                        <TableCell>{stock["Company Name"]}</TableCell>
                                        <TableCell>{stock.Sector}</TableCell>
                                        <TableCell>{stock.Industry}</TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>

                <Box sx={{ mt: 4 }}>
                    <Tabs
                        value={tab}
                        onChange={handleTabChange}
                        textColor="primary"
                        indicatorColor="primary"
                        sx={{
                            backgroundColor: "#f5f5f5",
                            borderRadius: 2,
                            boxShadow: 1,
                        }}
                    >
                        <Tab label="Single Stock Investment" />
                        <Tab label="Bulk Stock Investment" />
                    </Tabs>

                    <Box sx={{ mt: 3 }}>
                        <TabPanel value={tab} index={0}>
                            <SingleStockInvestment
                                momentumStocks={momentumStocks}
                                selectedStock={selectedStock}
                                selectedStockCurrentPrice={selectedStockCurrentPrice}
                                investmentAmount={investmentAmount}
                                quantity={quantity}
                                totalValue={totalValue}
                                orderMessage={orderMessage}
                                severity={severity}
                                handleSelectSell={handleSelectSell}
                                handleInvestmentAmount={handleInvestmentAmount}
                                handlePurchaseSingle={handlePurchaseSingle}
                            />
                        </TabPanel>
                        <TabPanel value={tab} index={1}>
                            <BulkStockInvestment
                                bulkAmount={bulkAmount}
                                setBulkAmount={setBulkAmount}
                                momentumStocks={momentumStocks}
                                generatePreview={generatePreview}
                                previewData={previewData}
                                totalInvestment={totalInvestment}
                                remaining={remaining}
                                orderMessage={orderMessage}
                                severity={severity}
                                handlePurchaseAll={handlePurchaseAll}
                            />
                        </TabPanel>
                    </Box>
                </Box>

                {failedOrders.length > 0 && <Box
                    sx={{
                        mt: 6,
                        p: 3,
                        backgroundColor: "#fafafa",
                        borderRadius: 2,
                        boxShadow: 2,
                        borderLeft: "5px solid #2d4890",
                    }}
                >
                    <Typography variant="h5" gutterBottom>
                        Unsuccessful Stock Orders
                    </Typography>


                    <Box component="ul" sx={{ pl: 3, listStyleType: "disc" }}>
                        {failedOrders.map((stock) => (
                            <Box component="li" key={stock.symbol} sx={{ mb: 1 }}>
                                <Typography variant="body1">
                                    <strong>{stock.symbol}</strong> – Message : {stock.error}
                                </Typography>
                            </Box>
                        ))}
                    </Box>

                </Box>}

                <Box
                    sx={{
                        mt: 6,
                        p: 3,
                        backgroundColor: "#fafafa",
                        borderRadius: 2,
                        boxShadow: 2,
                        borderLeft: "5px solid #2d4890",
                    }}
                >
                    <Typography variant="h5" gutterBottom>
                        Current Purchased Stocks Summary
                    </Typography>

                    {currentPurchased.length > 0 ? (
                        <Box component="ul" sx={{ pl: 3, listStyleType: "disc" }}>
                            <PurchasedStocks purchased={currentPurchased} />
                        </Box>
                    ) : (
                        <Typography color="text.secondary">No purchased stocks.</Typography>
                    )}
                </Box>
            </>
        );
    };







    return (
        <Box p={3} sx={{ backgroundColor: "white", minHeight: "100vh", position: "relative" }}>
            <Box display="flex" alignItems="center" mb={2}>
                <ShowChartIcon sx={{ color: "#2d4890", mr: 1 }} />
                <Typography variant="h5" sx={{ color: "#2d4890", fontWeight: "bold" }}>
                    Momentum Strategy Selector
                </Typography>
            </Box>
            {loading && (
                <Box
                    position="absolute"
                    top={350}
                    left={0}
                    width="100%"
                    height="100%"
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    zIndex={1000}
                    bgcolor="rgba(255, 255, 255, 0.6)" // optional dim background
                >
                    <CircularProgress size={40} />
                </Box>
            )}
            <Box sx={{ width: '100%', overflowX: 'hidden' }}>
                <Box sx={{ maxWidth: 2000, mx: "auto", width: "100%" }}> {AvailableStocks()}</Box>
            </Box>
        </Box>

    );
};

export default MomentumTrading;
